import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface PlanData {
  id: string;
  name: string;
  plan_key: string;
  tag: string;
  price: string;
  period: string;
  price_raw: number;
  messages_per_month: string;
  message_limit: number;
  users: string;
  whatsapps: string;
  features: string[];
  highlighted: boolean;
  cta: string;
  connection_label: string;
  excess_block_price: number;
  excess_block_size: number;
  setup_fee: number;
  sort_order: number;
  limit_whatsapps: number;
  limit_contacts: number;
  limit_campaigns: number;
  limit_users: number;
  priority_queue: boolean;
  support_level: string;
}

const emptyPlan: PlanData = {
  id: '', name: '', plan_key: '', tag: '', price: '', period: '', price_raw: 0,
  messages_per_month: '', message_limit: 0, users: '', whatsapps: '',
  features: [], highlighted: false, cta: '',
  connection_label: '', excess_block_price: 0, excess_block_size: 0, setup_fee: 0,
  sort_order: 0, limit_whatsapps: 1, limit_contacts: 500, limit_campaigns: 10,
  limit_users: 1, priority_queue: false, support_level: 'email',
};

const supportOptions = [
  { value: 'email', label: 'Email' },
  { value: 'chat', label: 'Chat' },
  { value: 'priority', label: 'Prioritário' },
  { value: 'dedicated', label: 'Dedicado 24/7' },
];

export default function AdminPlansPage() {
  const { profile } = useAuth();
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingPlan, setEditingPlan] = useState<PlanData | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<PlanData>(emptyPlan);
  const [featuresInput, setFeaturesInput] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    setError('');
    const { data, error: fetchError } = await supabase
      .from('plans')
      .select('*')
      .order('sort_order', { ascending: true });

    if (fetchError) {
      setError(fetchError.message);
    } else {
      setPlans((data || []) as PlanData[]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  if (profile && profile.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  const openCreate = () => {
    setIsCreating(true);
    setEditingPlan(null);
    setFormData({ ...emptyPlan, sort_order: plans.length });
    setFeaturesInput('');
    setSuccessMsg('');
  };

  const openEdit = (plan: PlanData) => {
    setIsCreating(false);
    setEditingPlan(plan);
    setFormData({ ...plan });
    setFeaturesInput((plan.features || []).join('\n'));
    setSuccessMsg('');
  };

  const cancelForm = () => {
    setIsCreating(false);
    setEditingPlan(null);
    setFormData(emptyPlan);
    setFeaturesInput('');
    setSuccessMsg('');
  };

  const handleSave = async () => {
    const features = featuresInput
      .split('\n')
      .map((f) => f.trim())
      .filter(Boolean);

    setSaving(true);
    setError('');

    const payload = {
      name: formData.name,
      plan_key: formData.plan_key || formData.name.toLowerCase().replace(/\s+/g, '-'),
      tag: formData.tag,
      price: formData.price,
      period: formData.period,
      price_raw: formData.price_raw,
      messages_per_month: formData.messages_per_month,
      message_limit: formData.message_limit,
      users: formData.users,
      whatsapps: formData.whatsapps,
      features,
      highlighted: formData.highlighted,
      cta: formData.cta,
      connection_label: formData.connection_label,
      excess_block_price: formData.excess_block_price,
      excess_block_size: formData.excess_block_size,
      setup_fee: formData.setup_fee,
      sort_order: formData.sort_order,
      limit_whatsapps: formData.limit_whatsapps,
      limit_contacts: formData.limit_contacts,
      limit_campaigns: formData.limit_campaigns,
      limit_users: formData.limit_users,
      priority_queue: formData.priority_queue,
      support_level: formData.support_level,
    };

    if (isCreating) {
      const { error: insertErr } = await supabase.from('plans').insert(payload);
      if (insertErr) {
        setError(insertErr.message);
        setSaving(false);
        return;
      }
      setSuccessMsg(`Plano "${payload.name}" criado com sucesso!`);
    } else if (editingPlan) {
      const { error: updateErr } = await supabase
        .from('plans')
        .update(payload)
        .eq('id', editingPlan.id);
      if (updateErr) {
        setError(updateErr.message);
        setSaving(false);
        return;
      }
      setSuccessMsg(`Plano "${payload.name}" atualizado com sucesso!`);
    }

    setSaving(false);
    setIsCreating(false);
    setEditingPlan(null);
    setFormData(emptyPlan);
    setFeaturesInput('');
    fetchPlans();
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleDelete = async (id: string) => {
    const plan = plans.find((p) => p.id === id);
    setSaving(true);
    const { error: delErr } = await supabase.from('plans').delete().eq('id', id);
    setSaving(false);
    if (delErr) {
      setError(delErr.message);
      return;
    }
    setDeleteConfirm(null);
    setSuccessMsg(`Plano "${plan?.name}" excluído.`);
    fetchPlans();
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const updateField = (field: keyof PlanData, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isFormOpen = isCreating || editingPlan !== null;

  const inputClass = "w-full px-3 py-2 bg-white dark:bg-background-100 border border-background-200 dark:border-background-300 rounded-lg text-sm text-foreground-900 dark:text-foreground-100 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all";
  const labelClass = "block text-xs font-semibold text-foreground-600 dark:text-foreground-400 mb-1.5";

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground-950 dark:text-foreground-100 mb-1">
            Gerenciar Planos
          </h1>
          <p className="text-sm text-foreground-500 dark:text-foreground-400">
            Crie, edite e remova os planos de assinatura com todos os limites e permissões
          </p>
        </div>
        {!isFormOpen && (
          <button
            onClick={openCreate}
            className="px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-xl transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line text-lg" />
            Novo Plano
          </button>
        )}
      </div>

      {successMsg && (
        <div className="bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 rounded-xl px-4 py-3 flex items-center gap-3">
          <i className="ri-check-line text-emerald-500 text-lg" />
          <p className="text-sm text-emerald-700 dark:text-emerald-400">{successMsg}</p>
        </div>
      )}
      {error && (
        <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl px-4 py-3 flex items-center gap-3">
          <i className="ri-error-warning-line text-red-500 text-lg" />
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {isFormOpen && (
        <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-heading font-bold text-foreground-950 dark:text-foreground-100">
              {isCreating ? 'Novo Plano' : `Editando: ${editingPlan?.name}`}
            </h3>
            <button onClick={cancelForm} className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:bg-background-100 hover:text-foreground-600 transition-colors cursor-pointer">
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {/* ─── Básico ─── */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Informações Básicas</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Nome do plano *</label>
                <input type="text" value={formData.name} onChange={(e) => updateField('name', e.target.value)} placeholder="Ex: Starter" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Chave (plan_key) *</label>
                <input type="text" value={formData.plan_key} onChange={(e) => updateField('plan_key', e.target.value)} placeholder="Ex: starter" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Preço exibido *</label>
                <input type="text" value={formData.price} onChange={(e) => updateField('price', e.target.value)} placeholder="Ex: R$ 97" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Valor numérico (R$) *</label>
                <input type="number" value={formData.price_raw} onChange={(e) => updateField('price_raw', Number(e.target.value))} placeholder="Ex: 97" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Período</label>
                <input type="text" value={formData.period} onChange={(e) => updateField('period', e.target.value)} placeholder="Ex: /mês" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Tag / Badge</label>
                <input type="text" value={formData.tag} onChange={(e) => updateField('tag', e.target.value)} placeholder="Ex: Mais Popular" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Texto do botão</label>
                <input type="text" value={formData.cta} onChange={(e) => updateField('cta', e.target.value)} placeholder="Ex: Assinar Starter" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Setup Fee (R$)</label>
                <input type="number" value={formData.setup_fee} onChange={(e) => updateField('setup_fee', Number(e.target.value))} placeholder="Ex: 297" className={inputClass} />
              </div>
            </div>
          </div>

          {/* ─── Mensagens ─── */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Mensagens</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>Mensagens/mês (texto)</label>
                <input type="text" value={formData.messages_per_month} onChange={(e) => updateField('messages_per_month', e.target.value)} placeholder="Ex: 5.000 mensagens/mês" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Limite de mensagens</label>
                <input type="number" value={formData.message_limit} onChange={(e) => updateField('message_limit', Number(e.target.value))} placeholder="Ex: 5000" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Bloco excedente (msgs)</label>
                <input type="number" value={formData.excess_block_size} onChange={(e) => updateField('excess_block_size', Number(e.target.value))} placeholder="Ex: 10000" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Preço excedente (R$)</label>
                <input type="number" value={formData.excess_block_price} onChange={(e) => updateField('excess_block_price', Number(e.target.value))} placeholder="Ex: 50" className={inputClass} />
              </div>
            </div>
          </div>

          {/* ─── Limites do Plano ─── */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Limites do Plano</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className={labelClass}>Limite WhatsApps</label>
                <input type="number" value={formData.limit_whatsapps} onChange={(e) => updateField('limit_whatsapps', Number(e.target.value))} placeholder="Ex: 1" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Limite Contatos</label>
                <input type="number" value={formData.limit_contacts} onChange={(e) => updateField('limit_contacts', Number(e.target.value))} placeholder="Ex: 500" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Limite Campanhas</label>
                <input type="number" value={formData.limit_campaigns} onChange={(e) => updateField('limit_campaigns', Number(e.target.value))} placeholder="Ex: 10" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Limite Usuários</label>
                <input type="number" value={formData.limit_users} onChange={(e) => updateField('limit_users', Number(e.target.value))} placeholder="Ex: 1" className={inputClass} />
              </div>
            </div>
          </div>

          {/* ─── Benefícios ─── */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-3">Benefícios</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>WhatsApps (texto)</label>
                <input type="text" value={formData.whatsapps} onChange={(e) => updateField('whatsapps', e.target.value)} placeholder="Ex: 1 WhatsApp" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Usuários (texto)</label>
                <input type="text" value={formData.users} onChange={(e) => updateField('users', e.target.value)} placeholder="Ex: 1 usuário" className={inputClass} />
              </div>
              <div>
                <label className={labelClass}>Nível de Suporte</label>
                <select value={formData.support_level} onChange={(e) => updateField('support_level', e.target.value)} className={`${inputClass} cursor-pointer`}>
                  {supportOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Descrição da conexão</label>
                <input type="text" value={formData.connection_label} onChange={(e) => updateField('connection_label', e.target.value)} placeholder="Descrição sobre como o cliente conecta..." className={inputClass} />
              </div>
            </div>
          </div>

          {/* ─── Toggles ─── */}
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-3">
              <button type="button" role="switch" aria-checked={formData.highlighted} onClick={() => updateField('highlighted', !formData.highlighted)} className={`relative w-10 h-5.5 rounded-full transition-colors cursor-pointer ${formData.highlighted ? 'bg-primary-500' : 'bg-background-300 dark:bg-background-400'}`}>
                <span className={`absolute top-0.5 w-4.5 h-4.5 rounded-full bg-white shadow-sm transition-transform ${formData.highlighted ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
              </button>
              <div>
                <p className="text-sm font-medium text-foreground-800 dark:text-foreground-200">Plano em destaque</p>
                <p className="text-xs text-foreground-500">Exibe badge e borda destacada</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button type="button" role="switch" aria-checked={formData.priority_queue} onClick={() => updateField('priority_queue', !formData.priority_queue)} className={`relative w-10 h-5.5 rounded-full transition-colors cursor-pointer ${formData.priority_queue ? 'bg-accent-500' : 'bg-background-300 dark:bg-background-400'}`}>
                <span className={`absolute top-0.5 w-4.5 h-4.5 rounded-full bg-white shadow-sm transition-transform ${formData.priority_queue ? 'translate-x-[18px]' : 'translate-x-[2px]'}`} />
              </button>
              <div>
                <p className="text-sm font-medium text-foreground-800 dark:text-foreground-200">Fila Prioritária</p>
                <p className="text-xs text-foreground-500">Mensagens processadas antes dos planos sem prioridade</p>
              </div>
            </div>
          </div>

          {/* ─── Funcionalidades ─── */}
          <div>
            <label className={labelClass}>Funcionalidades (uma por linha)</label>
            <textarea value={featuresInput} onChange={(e) => setFeaturesInput(e.target.value)} rows={6} placeholder="Importação CSV de contatos&#10;Templates básicos&#10;Relatório de envio" className={`${inputClass} resize-none`} />
          </div>

          <div className="flex items-center gap-3 pt-3 border-t border-background-100 dark:border-background-200">
            <button onClick={cancelForm} className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 dark:hover:bg-background-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
            <button onClick={handleSave} disabled={saving || !formData.name || !formData.price || !formData.plan_key} className="px-5 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-40 text-white text-sm font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer ml-auto flex items-center gap-2">
              {saving ? <><i className="ri-loader-4-line animate-spin" /> Salvando...</> : <><i className={isCreating ? 'ri-add-line' : 'ri-save-line'} />{isCreating ? 'Criar Plano' : 'Salvar Alterações'}</>}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-foreground-500">Carregando planos...</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 dark:border-background-200">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider">Plano</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-foreground-500 uppercase tracking-wider">Preço</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden md:table-cell">Msgs</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden lg:table-cell">Contatos</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden lg:table-cell">Campanhas</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden xl:table-cell">Usuários</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden xl:table-cell">Whats</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider hidden xl:table-cell">Suporte</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider">Fila</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-foreground-500 uppercase tracking-wider">Destaque</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-foreground-500 uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => (
                  <tr key={plan.id} className={`border-b border-background-50 dark:border-background-200 hover:bg-background-50/60 dark:hover:bg-background-200/50 transition-colors ${plan.highlighted ? 'bg-primary-50/20 dark:bg-primary-500/3' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${plan.highlighted ? 'bg-primary-100 dark:bg-primary-500/15' : 'bg-background-100 dark:bg-background-200'}`}>
                          <i className={`ri-vip-crown-line text-xs ${plan.highlighted ? 'text-primary-600 dark:text-primary-400' : 'text-foreground-500'}`} />
                        </div>
                        <div>
                          <p className="font-semibold text-foreground-900 dark:text-foreground-100 text-sm">{plan.name}</p>
                          <p className="text-[10px] text-foreground-400">{plan.plan_key}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-semibold text-foreground-800 dark:text-foreground-200 text-sm">{plan.price}</span>
                      <span className="text-foreground-500 text-[10px] ml-0.5">{plan.period}</span>
                    </td>
                    <td className="px-4 py-3 text-center hidden md:table-cell">
                      <span className="text-sm text-foreground-600 dark:text-foreground-400">{plan.message_limit.toLocaleString('pt-BR')}</span>
                    </td>
                    <td className="px-4 py-3 text-center hidden lg:table-cell">
                      <span className="text-sm text-foreground-600 dark:text-foreground-400">{plan.limit_contacts.toLocaleString('pt-BR')}</span>
                    </td>
                    <td className="px-4 py-3 text-center hidden lg:table-cell">
                      <span className="text-sm text-foreground-600 dark:text-foreground-400">{plan.limit_campaigns}</span>
                    </td>
                    <td className="px-4 py-3 text-center hidden xl:table-cell">
                      <span className="text-sm text-foreground-600 dark:text-foreground-400">{plan.limit_users}</span>
                    </td>
                    <td className="px-4 py-3 text-center hidden xl:table-cell">
                      <span className="text-sm text-foreground-600 dark:text-foreground-400">{plan.limit_whatsapps}</span>
                    </td>
                    <td className="px-4 py-3 text-center hidden xl:table-cell">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        plan.support_level === 'dedicated' ? 'bg-accent-100 text-accent-700' :
                        plan.support_level === 'priority' ? 'bg-primary-100 text-primary-700' :
                        plan.support_level === 'chat' ? 'bg-emerald-100 text-emerald-700' :
                        'bg-background-100 text-foreground-500'
                      }`}>
                        {supportOptions.find((o) => o.value === plan.support_level)?.label || plan.support_level}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {plan.priority_queue ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full text-[10px] font-medium">
                          <i className="ri-flashlight-fill text-[10px]" />Sim
                        </span>
                      ) : <span className="text-foreground-400 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {plan.highlighted ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-[10px] font-medium">
                          <i className="ri-star-fill text-[10px]" />Sim
                        </span>
                      ) : <span className="text-foreground-400 text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(plan)} className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:text-primary-400 dark:hover:bg-primary-500/10 transition-colors cursor-pointer" title="Editar">
                          <i className="ri-edit-line text-sm" />
                        </button>
                        <button onClick={() => setDeleteConfirm(plan.id)} className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-600 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-500/10 transition-colors cursor-pointer" title="Excluir">
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {plans.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-5 py-12 text-center">
                      <i className="ri-vip-crown-line text-4xl text-foreground-300 mb-3 block" />
                      <p className="text-sm text-foreground-500">Nenhum plano cadastrado</p>
                      <p className="text-xs text-foreground-400 mt-1">Crie o primeiro plano para começar</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-5 py-3 border-t border-background-100 dark:border-background-200 flex items-center justify-between">
          <p className="text-xs text-foreground-500">{plans.length} plano{plans.length !== 1 ? 's' : ''} cadastrado{plans.length !== 1 ? 's' : ''}</p>
          <p className="text-[10px] text-foreground-400">Arraste para reordenar (em breve)</p>
        </div>
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 w-full max-w-[400px] mx-4 p-6 animate-fadeIn">
            <div className="w-12 h-12 rounded-full bg-red-50 dark:bg-red-500/10 flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-2xl text-red-500" />
            </div>
            <h3 className="text-base font-heading font-semibold text-foreground-950 dark:text-foreground-100 text-center mb-2">Excluir plano</h3>
            <p className="text-sm text-foreground-500 text-center mb-6">
              Tem certeza que deseja excluir "{plans.find((p) => p.id === deleteConfirm)?.name}"? Essa ação não pode ser desfeita.
            </p>
            <div className="flex items-center gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 dark:hover:bg-background-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
              <button onClick={() => handleDelete(deleteConfirm)} disabled={saving} className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap">{saving ? 'Excluindo...' : 'Excluir'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}